import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  LinkIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';
import HostPicker, { useSamplePlayer } from './HostPicker';
import {
  FILE_ACCEPT,
  FILE_MAX_BYTES,
  MAX_SECONDS,
  MIN_SECONDS,
  SOURCE_TYPES,
  TEXT_MAX_CHARS,
  TEXT_MIN_CHARS,
  TITLE_MAX_CHARS,
  buildPodcastPayload,
  normalizeText,
  validatePodcastForm
} from './podcastJob';

const SOURCE_ICONS = { url: LinkIcon, text: DocumentTextIcon, file: DocumentArrowUpIcon };

const Section = ({ title, description, children }) => (
  <section className="rounded-xl border border-neutral-200 bg-white p-6">
    <h2 className="text-base font-medium text-gray-900">{title}</h2>
    {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-1 text-sm text-red-600">
      {message}
    </p>
  ) : null;

const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PodcastForm = ({ config, submitting, onSubmit }) => {
  const { t, i18n } = useTranslation();
  const player = useSamplePlayer();
  const presetSeconds = useMemo(
    () => config.durations.map((duration) => Number(duration.seconds)).filter(Number.isInteger),
    [config.durations]
  );

  const [values, setValues] = useState(() => ({
    sourceType: 'url',
    url: '',
    text: '',
    title: '',
    file: null,
    language: config.defaults.language,
    tone: config.defaults.tone,
    seconds: config.defaults.seconds,
    hostA: config.defaultHosts.A,
    hostB: config.defaultHosts.B
  }));
  const [customSeconds, setCustomSeconds] = useState(() => !presetSeconds.includes(config.defaults.seconds));
  const [errors, setErrors] = useState({});

  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field] && !(field.startsWith('host') && current.hosts)) return current;
      const next = { ...current };
      delete next[field];
      if (field.startsWith('host')) delete next.hosts;
      return next;
    });
  };

  const errorText = (field) => (errors[field] ? t(errors[field], {
    min: TEXT_MIN_CHARS,
    max: TEXT_MAX_CHARS,
    titleMax: TITLE_MAX_CHARS,
    minSeconds: MIN_SECONDS,
    maxSeconds: MAX_SECONDS,
    maxMb: Math.round(FILE_MAX_BYTES / (1024 * 1024))
  }) : null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const found = validatePodcastForm(values, config);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    await onSubmit(buildPodcastPayload(values));
  };

  const textLength = normalizeText(values.text).length;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6" aria-label={t('podcasts.form.title')}>
      <Section title={t('podcasts.form.sourceSection')} description={t('podcasts.form.sourceHint')}>
        <div className="inline-flex flex-wrap gap-2" role="group" aria-label={t('podcasts.form.sourceSection')}>
          {SOURCE_TYPES.map((type) => {
            const Icon = SOURCE_ICONS[type];
            const active = values.sourceType === type;
            return (
              <button
                key={type}
                type="button"
                aria-pressed={active}
                onClick={() => update('sourceType', type)}
                disabled={submitting}
                className={`btn btn-sm ${active ? 'btn-primary' : 'btn-secondary'}`}
              >
                <Icon className="h-4 w-4 mr-2" aria-hidden="true" />
                {t(`podcasts.sourceTypes.${type}`)}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          {values.sourceType === 'url' && (
            <div>
              <label htmlFor="podcast-url" className="block text-sm font-medium text-gray-700 mb-1">
                {t('podcasts.form.urlLabel')}
              </label>
              <input
                id="podcast-url"
                type="url"
                inputMode="url"
                autoComplete="url"
                value={values.url}
                onChange={(event) => update('url', event.target.value)}
                placeholder={t('podcasts.form.urlPlaceholder')}
                disabled={submitting}
                aria-invalid={errors.url ? 'true' : 'false'}
                aria-describedby={errors.url ? 'podcast-url-error' : undefined}
                className={errors.url ? 'input-error' : ''}
              />
              <FieldError id="podcast-url-error" message={errorText('url')} />
            </div>
          )}

          {values.sourceType === 'text' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="podcast-title" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('podcasts.form.titleLabel')}
                </label>
                <input
                  id="podcast-title"
                  type="text"
                  value={values.title}
                  maxLength={TITLE_MAX_CHARS}
                  onChange={(event) => update('title', event.target.value)}
                  placeholder={t('podcasts.form.titlePlaceholder')}
                  disabled={submitting}
                  aria-invalid={errors.title ? 'true' : 'false'}
                  className={errors.title ? 'input-error' : ''}
                />
                <FieldError id="podcast-title-error" message={errorText('title')} />
              </div>
              <div>
                <label htmlFor="podcast-text" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('podcasts.form.textLabel')}
                </label>
                <textarea
                  id="podcast-text"
                  rows={10}
                  value={values.text}
                  onChange={(event) => update('text', event.target.value)}
                  placeholder={t('podcasts.form.textPlaceholder')}
                  disabled={submitting}
                  aria-invalid={errors.text ? 'true' : 'false'}
                  aria-describedby="podcast-text-counter"
                  className={`w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary-500 focus:ring-primary-500 ${errors.text ? 'input-error' : ''}`}
                />
                <p
                  id="podcast-text-counter"
                  className={`mt-1 text-xs ${textLength > 0 && (textLength < TEXT_MIN_CHARS || textLength > TEXT_MAX_CHARS) ? 'text-red-600' : 'text-gray-500'}`}
                >
                  {t('podcasts.form.textCounter', {
                    count: textLength,
                    min: TEXT_MIN_CHARS,
                    max: TEXT_MAX_CHARS.toLocaleString(i18n?.language || 'pl')
                  })}
                </p>
                <FieldError id="podcast-text-error" message={errorText('text')} />
              </div>
            </div>
          )}

          {values.sourceType === 'file' && (
            <div>
              <label htmlFor="podcast-file" className="block text-sm font-medium text-gray-700 mb-1">
                {t('podcasts.form.fileLabel')}
              </label>
              <input
                id="podcast-file"
                type="file"
                accept={FILE_ACCEPT}
                onChange={(event) => update('file', event.target.files && event.target.files[0] ? event.target.files[0] : null)}
                disabled={submitting}
                aria-invalid={errors.file ? 'true' : 'false'}
                className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('podcasts.form.fileHint', { maxMb: Math.round(FILE_MAX_BYTES / (1024 * 1024)) })}
              </p>
              {values.file && (
                <p className="mt-1 text-sm text-gray-700" data-testid="podcast-file-name">
                  {values.file.name} ({formatFileSize(values.file.size)})
                </p>
              )}
              <FieldError id="podcast-file-error" message={errorText('file')} />
            </div>
          )}
        </div>
      </Section>

      <Section title={t('podcasts.form.languageToneSection')}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <label htmlFor="podcast-language" className="block text-sm font-medium text-gray-700 mb-1">
              {t('podcasts.form.languageLabel')}
            </label>
            <select
              id="podcast-language"
              value={values.language}
              onChange={(event) => update('language', event.target.value)}
              disabled={submitting}
            >
              {config.languages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">{t('podcasts.form.languageHint')}</p>
            <FieldError id="podcast-language-error" message={errorText('language')} />
          </div>
          <fieldset className="md:col-span-2">
            <legend className="block text-sm font-medium text-gray-700 mb-1">{t('podcasts.form.toneLabel')}</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {config.tones.map((tone) => {
                const selected = values.tone === tone.code;
                return (
                  <label
                    key={tone.code}
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm ${
                      selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="podcast-tone"
                      value={tone.code}
                      checked={selected}
                      onChange={() => update('tone', tone.code)}
                      disabled={submitting}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-900">
                        {t(`podcasts.tones.${tone.code}`, { defaultValue: tone.label })}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t(`podcasts.toneHints.${tone.code}`, { defaultValue: '' })}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <FieldError id="podcast-tone-error" message={errorText('tone')} />
          </fieldset>
        </div>
      </Section>

      <Section title={t('podcasts.form.hostsSection')} description={t('podcasts.form.hostsHint')}>
        <div className="space-y-6">
          <HostPicker
            role="A"
            hosts={config.hosts}
            value={values.hostA}
            otherValue={values.hostB}
            onChange={(id) => update('hostA', id)}
            disabled={submitting}
            player={player}
          />
          <HostPicker
            role="B"
            hosts={config.hosts}
            value={values.hostB}
            otherValue={values.hostA}
            onChange={(id) => update('hostB', id)}
            disabled={submitting}
            player={player}
          />
        </div>
        <FieldError id="podcast-hosts-error" message={errorText('hosts')} />
      </Section>

      <Section title={t('podcasts.form.lengthSection')} description={t('podcasts.form.lengthHint')}>
        <fieldset>
          <legend className="sr-only">{t('podcasts.form.lengthLabel')}</legend>
          <div className="flex flex-wrap gap-2">
            {presetSeconds.map((seconds) => {
              const selected = !customSeconds && Number(values.seconds) === seconds;
              return (
                <label
                  key={seconds}
                  className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium ${
                    selected ? 'border-primary-500 bg-primary-50 text-primary-700 ring-1 ring-primary-500' : 'border-gray-200 text-gray-700 hover:border-primary-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="podcast-length"
                    value={seconds}
                    checked={selected}
                    onChange={() => {
                      setCustomSeconds(false);
                      update('seconds', seconds);
                    }}
                    disabled={submitting}
                    className="sr-only"
                  />
                  {t('podcasts.form.minutes', { count: seconds / 60 })}
                </label>
              );
            })}
            <label
              className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium ${
                customSeconds ? 'border-primary-500 bg-primary-50 text-primary-700 ring-1 ring-primary-500' : 'border-gray-200 text-gray-700 hover:border-primary-300'
              }`}
            >
              <input
                type="radio"
                name="podcast-length"
                value="custom"
                checked={customSeconds}
                onChange={() => setCustomSeconds(true)}
                disabled={submitting}
                className="sr-only"
              />
              {t('podcasts.form.customLength')}
            </label>
          </div>
          {customSeconds && (
            <div className="mt-3 max-w-xs">
              <label htmlFor="podcast-seconds" className="block text-sm font-medium text-gray-700 mb-1">
                {t('podcasts.form.secondsLabel', { min: MIN_SECONDS, max: MAX_SECONDS })}
              </label>
              <input
                id="podcast-seconds"
                type="number"
                min={MIN_SECONDS}
                max={MAX_SECONDS}
                step={1}
                value={values.seconds}
                onChange={(event) => update('seconds', event.target.value === '' ? '' : Number(event.target.value))}
                disabled={submitting}
                aria-invalid={errors.seconds ? 'true' : 'false'}
                className={errors.seconds ? 'input-error' : ''}
              />
            </div>
          )}
          <FieldError id="podcast-seconds-error" message={errorText('seconds')} />
        </fieldset>
      </Section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button type="submit" disabled={submitting} className={`btn btn-md ${submitting ? 'btn-muted' : 'btn-primary'}`}>
          <MicrophoneIcon className="h-5 w-5 mr-2" aria-hidden="true" />
          {submitting ? t('podcasts.form.submitting') : t('podcasts.form.submit')}
        </button>
        {hasErrors && <span className="text-sm text-red-600">{t('podcasts.form.fixErrors')}</span>}
      </div>
    </form>
  );
};

export default PodcastForm;
